<script type="ts">
    import FaPlusSquare from "svelte-icons/fa/FaPlusSquare.svelte";
    import Button from "./button.svelte";
    import { fade } from "svelte/transition";
    import FaSortDown from "svelte-icons/fa/FaSortDown.svelte";
    import FaRegTimesCircle from "svelte-icons/fa/FaRegTimesCircle.svelte";
    import { createEventDispatcher } from "svelte";
    import { t, locale, findKeyByValue } from "../translations/i18n";
    import translations from "../translations/translation";

    export let isOpen = true;

    /* Variables for storing state of dropdowns */
    let minDropdownOpen = false;
    let maxDropdownOpen = false;

    /* Variables for current storing period selections */
    let selectedFrom = '';
    let selectedTo = '';

    /* Variable for pass values to parent component (Chart, Table, ...) */
    const dispatch = createEventDispatcher();

    /* List of months for dropdowns */
    const dateList = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
    ];

    /**
     * Handler function for closing period selector when close button is pressed
    */
    const closePeriodSelector = (): void => {
        minDropdownOpen = false;
        maxDropdownOpen = false;
        isOpen = false;
        selectedFrom = '';
        selectedTo = '';
        const selectedValues = { selectedFrom, selectedTo };
        dispatch('close', selectedValues);
        let container = document.querySelector('.period-selector-container') as HTMLElement;
        if (container){
            container.style.display = 'none';
        }
    }

    /**
     * Handler function for opening/closing dropdowns
    */
    const toggleDropdownHandler = (event: Event, type: string): void => {

        event.stopPropagation();

        let opening = false;

        if (type === 'min') {
            opening = !minDropdownOpen;
        } else 
        if (type === 'max') {
            opening = !maxDropdownOpen;
        }

        document.body.click(); // click "outside" to close dropdowns
        if (opening) {
            if (type === 'min') {
                minDropdownOpen = true;
            } else 
            if (type === 'max') {
                maxDropdownOpen = true;
            }
        }
    };

    /**
     * Handler function for storing selected period options
    */
    const chooseOptionHandler = (event: Event, type: string): void => {

        event.stopPropagation();

        const element = event.target as HTMLElement;
        let selectedOption = element.textContent ? element.textContent.trim() : '';
        selectedOption = findKeyByValue(translations[$locale], element.textContent);

        if (type === 'min') {
            selectedFrom = selectedOption;
            minDropdownOpen = false;
        }
        else if (type === 'max') {
            selectedTo = selectedOption;
            maxDropdownOpen = false;
        }
    };

    /**
     * Function for set and pass the selected period and closing period selector
    */
    const applyHandler = (): void => {
        minDropdownOpen = false;
        maxDropdownOpen = false;
        isOpen = false;
        const selectedValues = { selectedFrom, selectedTo };

        // Pass selected period to parent component (Chart, Table, ...)
        dispatch('close', selectedValues);

        // Close period selector when apply button is pressed
        let container = document.querySelector('.period-selector-container') as HTMLElement;
        if (container){
            container.style.display = 'none';
        }
    };
    
    /**
     * Function for closing dropdowns when click outside 
    */
    const closeDropdownOutsideHandler = (event: MouseEvent): void => {
        const dropdownContainer = document.querySelector('.period-selector-container');
        const target = event.target as HTMLElement;

        if (dropdownContainer && !dropdownContainer.contains(target)) {
            minDropdownOpen = false;
            maxDropdownOpen = false;
        }
    };

    /* Listening for clicks outside the period selector */
    window.addEventListener('click', closeDropdownOutsideHandler);

</script>

<div class="period-selector-container">
    <div class="close-button-container">
        <div class="button-wrapper" on:click={closePeriodSelector} title={$t("Remove")}>
            <FaRegTimesCircle />
        </div>
    </div>
    <div class="dropdown-container">
        <div class="dropdown" on:click|stopPropagation={event => toggleDropdownHandler(event, 'min')} data-testid="icon-dropdown-test">
            {#if selectedFrom}
                {$t(selectedFrom)}
            {:else}
            <div class="label">
                <div>
                    {$t("From")}
                </div>
            </div>
            <div class="icon">
                <FaSortDown />
            </div>
            {/if}
            {#if minDropdownOpen}
                <ul>
                {#each dateList as option}
                    <li
                        value={$t(option)}
                        on:click|stopPropagation={event => chooseOptionHandler(event, 'min')}
                    >{$t(option)}</li>
                {/each}
                </ul>
            {/if}
        </div>
        <div class="dropdown" on:click|stopPropagation={event => toggleDropdownHandler(event, 'max')} data-testid="icon-dropdown-test">
            {#if selectedTo}
                {$t(selectedTo)}
            {:else}
            <div class="label">
                <div>
                    {$t("To")}
                </div>
            </div>
            <div class="icon">
                <FaSortDown />
            </div>
            {/if}
            {#if maxDropdownOpen}
                <ul>
                {#each dateList as option}
                    <li
                        value={$t(option)}
                        on:click|stopPropagation={event => chooseOptionHandler(event, 'max')}
                    >{$t(option)}</li>
                {/each}
                </ul>
            {/if}
        </div>
    </div>
    <div class="button-container" transition:fade>
        <div>
            <Button
                handler={applyHandler}
                iconOnTheLeft={false}
                disabled={false}
                title={$t("Select")}
                id="customePeriodApplyButton"
            >
                <FaPlusSquare slot="icon" />
                <svelte:fragment slot="text">{$t("Select")}</svelte:fragment>
            </Button>
        </div>
    </div>
</div>

<style lang="scss">
    .period-selector-container {
        width: 25vw;
        max-width: 350px;
        position: absolute;
        top: 12rem;
        left: 50%;
        transform: translate(-50%, -50%);
        background-color: white;
        border: 1px solid var(--color-primary);
        border-radius: 5px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
        padding: 10px;
        z-index: 9999;
        gap: 0.5rem;

        display: flex;
        flex-direction: column;
    }
    .close-button-container {
        position: relative;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        padding: 2px;
        color: var(--color-tertiary);

        .button-wrapper {
            cursor: pointer;
            height: 1.25rem;
            width: 1.25rem;
        }
    }

    .dropdown-container {
        display: flex;
        position: relative;
        flex-direction: row;
        justify-content: space-between;
        margin: 0.75rem 0.5rem;
        gap: 20px;

        .dropdown {
            position: relative;
            padding: 0.75rem 1.5rem;
            width: 6rem;
            height: 1rem;

            display: inline-block;
            justify-content: center;
            text-align: center;
            align-items: center;
            gap: 0.5rem;

            border-radius: 0.5rem;
            border: 1px solid var(--color-gray);
            cursor: pointer;
            font-size: 1rem;
            line-height: 1;
            font-weight: bold;

            background-color: #F1F3F4;
            color: var(--color-black);
            user-select: none;

            .label {
                display: flex;
                justify-content: center;
            }
            .icon {
                position: absolute;
                pointer-events: none;
                right: 0.5rem;
                top: 0.5rem;
            }
        }
    }

    .button-container {
        display: flex;
        padding: 10px;
        justify-content: center;
        align-items: center;
    }

    @import "../scrollbars";

    ul {
        position: absolute;
        z-index: 15;
        margin: 1rem 0 0 -25px;
        padding: 0;

        max-height: 180px;
        width: 100%;
        overflow-y: auto;
        overflow-x: hidden;

        display: flex;
        flex-direction: column;

        box-sizing: border-box;
        border: 1px solid var(--color-gray);
        color: var(--color-black);
        background-color: var(--color-white);

        border-radius: 0.25rem;

        list-style-type: none;
        font-size: 1rem;
        line-height: 1;

        li {
            padding: 0.5rem 1rem;
            white-space: nowrap;
            cursor: pointer;

            &:hover {
                color: var(--color-white);
                background-color: #35b472cc;
            }
        }
    }

</style>